<template>
    <div class="flex flex-1 flex-col">
        <app-base>
            <header class="border-b">
                <div
                    class="relative flex flex-col justify-center overflow-hidden bg-white"
                >
                    <div>
                        <div
                            class="relative mx-0 flex items-center justify-between gap-4 px-4 py-6 md:px-7 lg:static lg:px-16 lg:py-6"
                        >
                            <div class="flex items-center gap-8">
                                <organization-facility-logo />

                                <a
                                    :href="
                                        facilityHomeLink(activeFacility.slug)
                                    "
                                >
                                    <prime-button
                                        type="button"
                                        :label="t('layout.home')"
                                    />
                                </a>

                                <div class="font-semibold text-gray-600">
                                    {{ activeFacility.shortName }}
                                </div>
                            </div>

                            <div
                                class="flex w-full flex-1 items-center justify-end"
                            >
                                <ul class="flex items-center">
                                    <profile-buttons
                                        v-if="showProfileButtons"
                                        :profile-label="
                                            t('layout.customerAccount', {
                                                name: signedInCustomerName
                                            })
                                        "
                                        :show-sign-out="false"
                                    />

                                    <!-- Kiosks retain language access after the optional customer account action. -->
                                    <li><header-locale-switcher /></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <!-- Keep the active customer visible while kiosk content scrolls. -->
            <section
                v-if="viewer !== undefined"
                class="sticky top-0 z-20 w-full border-b border-gray-300 bg-gray-200 shadow-md"
            >
                <div
                    class="mx-auto flex max-w-screen-xl justify-center px-4 py-3 md:px-7 lg:px-16"
                >
                    <div
                        class="flex w-full max-w-xl flex-col overflow-hidden bg-white shadow-sm sm:flex-row"
                    >
                        <div
                            class="flex min-w-0 flex-1 items-center gap-3 px-6 py-3 text-2xl text-gray-900"
                        >
                            <i
                                class="pi pi-user text-lg"
                                aria-hidden="true"
                            ></i>
                            <i18n-t
                                keypath="layout.signedInAs"
                                scope="global"
                                tag="span"
                                class="truncate"
                            >
                                <template #name>
                                    <span class="font-semibold">
                                        {{ signedInCustomerName }}
                                    </span>
                                </template>
                            </i18n-t>
                        </div>

                        <div
                            class="flex w-full shrink-0 border-t border-gray-300 sm:w-auto sm:border-l sm:border-t-0"
                        >
                            <prime-button
                                type="button"
                                class="!h-full !w-full !rounded-none !border-0 sm:!w-auto"
                                :label="t('layout.notYouSignOut')"
                                :loading="isSigningOut"
                                @click="signOut"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <div class="flex flex-1 flex-col">
                <div
                    class="z-2 mx-0 my-2 flex-1 px-4 py-4 md:mx-6 md:my-6 lg:mx-8 lg:px-8"
                >
                    <div class="mx-auto flex max-w-screen-xl items-start gap-8">
                        <!-- duplicated div to try and fix some vue bugs -->
                        <div
                            v-if="checkoutResultData !== undefined"
                            class="w-full"
                        >
                            <checkout-success-kiosk
                                :checkout-result-data="checkoutResultData"
                                :completed-message="
                                    completionMessages?.facility
                                        ?.completedMessage ?? ''
                                "
                                :staff-activation-required-message="
                                    completionMessages?.facility
                                        ?.staffActivationRequiredMessage ?? ''
                                "
                                @reset="handleReset"
                            />
                        </div>

                        <div
                            v-else
                            class="w-full"
                        >
                            <slot />
                        </div>
                    </div>
                </div>

                <powered-by-footer>
                    <template #start>
                        <kiosk-verification />
                    </template>
                </powered-by-footer>
            </div>

            <kiosk-inactivity-timeout
                :idle-timeout="idleTimeout"
                :idle-timeout-message="idleTimeoutMessage"
            />

            <kiosk-plan-presentation-customer-confirmation
                :catalog="catalog"
                @sign-out="signOut"
            />
        </app-base>
    </div>
</template>

<script setup lang="ts">
import AppBase from '~/components/Layouts/AppBase.vue'
import PoweredByFooter from '~/components/PoweredByFooter.vue'
import OrganizationFacilityLogo from '~/components/Layouts/OrganizationFacilityLogo.vue'
import ProfileButtons from '~/components/Layouts/ProfileButtons.vue'
import HeaderLocaleSwitcher from '~/components/Layouts/HeaderLocaleSwitcher.vue'
import CheckoutSuccessKiosk from '~/components/Checkout/CheckoutSuccessKiosk.vue'
import KioskPlanPresentationCustomerConfirmation from '~/components/Layouts/StorefrontLayout/KioskPlanPresentationCustomerConfirmation.vue'
import KioskInactivityTimeout from '~/components/Layouts/StorefrontLayout/KioskInactivityTimeout.vue'
import KioskVerification from '~/components/Verification/KioskVerification.vue'
import type { StorefrontCatalogScalar } from '~/resources/scalars/storefront-catalog-scalar'
import PrimeButton from 'primevue/button'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useRouteLink } from '~/composables/routing'
import {
    useLogoutMutation,
    useSetViewer,
    useViewer
} from '~/composables/use-login'
import { useSetCartData } from '~/composables/use-cart'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import {
    type KioskSuccessData,
    provideKioskSuccessFunction
} from '~/composables/use-kiosk-success'
import { useSelectedLanguage } from '~/composables/use-selected-language'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useI18n, I18nT } from 'vue-i18n'

const props = defineProps<{
    catalog: StorefrontCatalogScalar
    storefrontPrefix: string
    profileAllowed: boolean
    idleTimeout: number
    idleTimeoutMessage: string
}>()
const { t } = useI18n({ useScope: 'global' })

const { mutate, isPending: isSigningOut } = useLogoutMutation()

const queryClient = useQueryClient()
const router = useRouter()
const activeFacility = useActiveFacility()
const facilitySlug = computed(() => activeFacility.value.slug)
const selectedLanguage = useSelectedLanguage()
const rpQuery = useRpQuery()
const { data: completionMessages } = useQuery({
    queryKey: ['KioskCompletionMessages', facilitySlug, selectedLanguage],
    queryFn: () =>
        rpQuery(
            graphql(/* GraphQL */ `
                query KioskCompletionMessages(
                    $slug: String!
                    $language: Language!
                ) {
                    facility(slug: $slug) {
                        completedMessage: kioskCompletionMarkup(
                            staffActivationRequired: false
                            language: $language
                        )
                        staffActivationRequiredMessage: kioskCompletionMarkup(
                            staffActivationRequired: true
                            language: $language
                        )
                    }
                }
            `),
            { slug: facilitySlug.value, language: selectedLanguage.value }
        )
})
const { facilityHomeLink } = useRouteLink()
const viewer = useViewer()

const setViewer = useSetViewer()
const setCartData = useSetCartData()
function signOut(destination?: string) {
    mutate(undefined, {
        onSuccess: data => {
            if (data.logout === true) {
                setViewer(undefined)
                setCartData(undefined)
                queryClient.invalidateQueries()
                router.push(
                    destination ?? facilityHomeLink(activeFacility.value.slug)
                )
            } else {
                throw new Error()
            }
        },
        onSettled() {
            queryClient.invalidateQueries()
        }
    })
}

const showProfileButtons = computed(() => {
    return props.profileAllowed
})

const signedInCustomerName = computed(() => {
    if (viewer.value === undefined) {
        return ''
    }

    // Kiosks identify the customer without exposing a full surname to passersby.
    return `${viewer.value.firstName} ${viewer.value.lastName.charAt(0)}.`
})

const checkoutResultData = ref<KioskSuccessData>()
provideKioskSuccessFunction(data => {
    checkoutResultData.value = data
    setViewer(undefined)
    setCartData(undefined)
})

// DEV ONLY: Useful helper to test the checkout result page
// useCheckoutResult((data: FragmentType<typeof CheckoutSuccessFragment>) => {
//     checkoutResultData.value = data
// })

function handleReset() {
    checkoutResultData.value = undefined
    queryClient.invalidateQueries()
    router.push(facilityHomeLink(activeFacility.value.slug))
    signOut()
}
</script>
